import { beispielDaten, javaAnsicht, type BaumPlugin, type BaumUi, type BaumWorkspace, type Katalog } from "@gui-creator/gemeinsam";
import { BookOpen, ListTodo } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { useKatalog, useStruktur, useUi } from "../api/abfragen.ts";
import { STATUS_ANZEIGE, StatusMarke } from "../baum/StatusMarke.tsx";
import { findePfad, findeUi, uiAdresse } from "../huelle/pfad.ts";
import { Inventar } from "../inventar/Inventar.tsx";
import { Notizen } from "../planung/Notizen.tsx";
import "./seiten.css";
import "./ueberblick.css";

function Miniatur({ id, katalog }: { id: string; katalog: Katalog }) {
  const ui = useUi(id);
  const struktur = useStruktur();
  const ansicht = useMemo(() => {
    if (!ui.data) return null;
    const baum = struktur.data;
    return javaAnsicht(ui.data.daten, katalog, beispielDaten(ui.data.daten, katalog), 0, (v) => (baum ? findeUi(baum, v)?.ui.schluessel : undefined));
  }, [ui.data, katalog, struktur.data]);
  if (!ansicht) return <div className="karte-bild karte-bild-leer" />;
  return (
    <div className="karte-bild" inert>
      <Inventar ansicht={ansicht} katalog={katalog} skala={1} />
    </div>
  );
}

function UiKarte({ plugin, workspace, ui, katalog }: { plugin: BaumPlugin; workspace: BaumWorkspace; ui: BaumUi; katalog: Katalog | undefined }) {
  return (
    <li className="karte">
      {katalog ? <Miniatur id={ui.id} katalog={katalog} /> : <div className="karte-bild karte-bild-leer" />}
      <div className="karte-text">
        <Link to={uiAdresse(plugin.schluessel, workspace.schluessel, ui.schluessel)} className="karte-link">
          {ui.name ?? ui.schluessel}
        </Link>
        {ui.name && <span className="mono leise">{ui.schluessel}</span>}
        <span className="karte-meta">
          <StatusMarke status={ui.status} /> {STATUS_ANZEIGE[ui.status].text}
          {ui.geaendertSeitStand && <span className="leise"> · seitdem geändert</span>}
          {ui.offeneTodos > 0 && (
            <span className="karte-todos">
              <ListTodo size={12} aria-hidden="true" /> {ui.offeneTodos}
            </span>
          )}
        </span>
      </div>
    </li>
  );
}

function UiRaster({ plugin, workspace, katalog }: { plugin: BaumPlugin; workspace: BaumWorkspace; katalog: Katalog | undefined }) {
  if (workspace.uis.length === 0) return <p className="seite-leer">Noch keine UIs. Im Baum links über „+“ anlegen.</p>;
  return (
    <ul className="karten">
      {workspace.uis.map((u) => (
        <UiKarte key={u.id} plugin={plugin} workspace={workspace} ui={u} katalog={katalog} />
      ))}
    </ul>
  );
}

function offen(w: BaumWorkspace): number {
  return w.uis.reduce((n, u) => n + u.offeneTodos, 0);
}

export function PluginUeberblick() {
  const p = useParams();
  const struktur = useStruktur();
  const plugin = struktur.data ? findePfad(struktur.data, { plugin: p.plugin }).plugin : null;
  const katalog = useKatalog(plugin?.id);
  if (struktur.data && !plugin) return <NichtDa was="Dieses Plugin" />;
  if (!plugin) return <main className="seite" aria-busy="true" />;
  return (
    <main className="seite seite-breit">
      <header className="seite-kopf seite-kopf-zeile">
        <div>
          <h1>{plugin.name}</h1>
          <p className="mono leise">{plugin.schluessel}</p>
        </div>
        <Link to={`/katalog/${plugin.schluessel}`} className="knopf">
          <BookOpen size={14} aria-hidden="true" /> Katalog
        </Link>
      </header>
      {plugin.workspaces.length === 0 && <p className="seite-leer">Noch keine Workspaces. Im Baum links über „+“ anlegen.</p>}
      {plugin.workspaces.map((w) => (
        <section key={w.id} className="ueberblick-gruppe" aria-labelledby={`ws-${w.id}`}>
          <h2 id={`ws-${w.id}`}>
            <Link to={`/p/${plugin.schluessel}/${w.schluessel}`}>{w.name}</Link>
            <span className="leise zahlen">
              {" "}
              · {w.uis.length} {w.uis.length === 1 ? "UI" : "UIs"}
              {offen(w) > 0 && ` · ${offen(w)} offene To-dos`}
            </span>
          </h2>
          <UiRaster plugin={plugin} workspace={w} katalog={katalog.data} />
        </section>
      ))}
      <div className="ueberblick-notizen">
        <Notizen ziel={{ art: "plugin", id: plugin.id }} />
      </div>
    </main>
  );
}

export function WorkspaceUeberblick() {
  const p = useParams();
  const struktur = useStruktur();
  const pfad = struktur.data ? findePfad(struktur.data, { plugin: p.plugin, workspace: p.workspace }) : null;
  const katalog = useKatalog(pfad?.plugin?.id);
  if (struktur.data && !pfad?.workspace) return <NichtDa was="Dieser Workspace" />;
  if (!pfad?.plugin || !pfad.workspace) return <main className="seite" aria-busy="true" />;
  const { plugin, workspace } = pfad;
  return (
    <main className="seite seite-breit">
      <header className="seite-kopf">
        <h1>{workspace.name}</h1>
        <p className="leise">
          <span className="mono">
            {plugin.schluessel}/{workspace.schluessel}
          </span>{" "}
          · {workspace.uis.length} {workspace.uis.length === 1 ? "UI" : "UIs"}
        </p>
      </header>
      <UiRaster plugin={plugin} workspace={workspace} katalog={katalog.data} />
      <div className="ueberblick-notizen">
        <Notizen ziel={{ art: "workspace", id: workspace.id }} />
      </div>
    </main>
  );
}

function NichtDa({ was }: { was: string }) {
  return (
    <main className="seite">
      <h1>{was} existiert nicht.</h1>
      <p className="leise">Vielleicht wurde es umbenannt oder gelöscht.</p>
    </main>
  );
}
